<template>
  <component
    :is="standalone ? 'div' : 'b-modal'"
    v-bind="standalone ? { class: 'mobile-route-map-page', dir: 'ltr' } : modalProps"
    v-on="standalone ? {} : modalEvents"
  >
    <base-spinner v-if="loading" />
    <div v-else class="map-modal-body">

      <div class="map-modal-header">
        <h5 v-if="$route.name !== 'mobile-route-map-embed'" class="modal-title">{{ $t('trip-status') || 'حالة الرحلة' }}</h5>
      </div>

      <b-alert :show="error && error.length ? true : false" variant="danger" class="mx-3 mt-2">
        <div v-if="Array.isArray(error)">
          <div class="d-flex flex-column my-1" v-for="(error_text, index) in error" :key="index">
            {{ error_text }}
          </div>
        </div>
        <div v-else>
          <div v-html="error || '&nbsp;'"></div>
        </div>
      </b-alert>

      <div class="map-canvas-wrap">
        <div id="mobile-route-map" style="height: 560px"></div>

        <div v-if="locationStale && !isTripCompleted" class="stale-banner">
          {{ $t('live-location-unavailable') || 'Live location unavailable — showing last known position' }}
        </div>

        <div class="zoom-controls">
          <button type="button" class="zoom-btn" @click="zoomIn" aria-label="zoom in">+</button>
          <div class="zoom-divider"></div>
          <button type="button" class="zoom-btn" @click="zoomOut" aria-label="zoom out">&minus;</button>
        </div>

      
        <div class="trip-status-card">
          <button type="button" class="back-arrow-btn" @click="$emit('back')" aria-label="back">
            &lsaquo;
          </button>

          <div class="status-card-body">
            <div class="status-card-top">
              <span class="status-badge" :class="`status-badge--${statusMeta.badgeVariant}`">
                {{ statusMeta.badgeText }}
              </span>
            </div>

            <div class="status-card-main">
              <span v-if="isTripCompleted" >
               <img src="../../assets/images/icons/watersource.png" alt="truk-illustration" class="completed-icon"/>
              </span>
              <span v-else class="truck-illustration">
                <img src="../../assets/images/icons/tanker2.svg" alt="truk-illustration"  class="completed-icon"/>
              </span>
              <div class="status-card-text">
                <p class="status-main-text">{{ statusMeta.mainText }}</p>
                <p v-if="statusMeta.subText" class="status-sub-text">{{ statusMeta.subText }}</p>
              </div>
            </div>

          
          </div>
        </div>
      </div>

      <div class="d-flex align-items-center justify-content-center flex-wrap p-3 gap-4 route-legend">
        <p class="route-name">{{ $t('mobile-route') }} :</p>
        <div
          v-for="(route, index) in route_labels"
          :key="index"
          class="legend-item d-flex align-items-center"
        >
          <span class="legend-swatch" :style="{ 'background-color': route.color }"></span>
          <span class="legend-label">{{ route.label }}</span>
        </div>
      </div>
    </div>
  </component>
</template>

<script>
import TripRouteService from '@/services/trip-route.service'
import mapboxgl from 'mapbox-gl'
import 'mapbox-gl/dist/mapbox-gl.css'
import moment from 'moment'
import { subscribeToTripLiveLocation, unsubscribeFromTripLiveLocation } from '@/socket.js'

let map, yard_marker, pickup_marker, dropoff_marker, truck_marker, truck_el, trip_start_marker

const ROUTE_COLORS = {
  NavigatingToPickup: '#3AA0FF',
  NavigatingToDropOff: '#0B1F63',
}


const TRIP_START_POINT_COLOR = '#DC2626'
const TRAVELED_PATH_COLOR = '#1E7D3C'
const SHOW_TRIP_START_MARKER = true
const DEBUG_OVERLAY = true
const LEG_DEBUG = true
const FALLBACK_AVG_SPEED_KMH = 30
const DEFAULT_ANIM_SPEED_KMH = 55
const MIN_ANIM_SPEED_KMH = 15
const MAX_ANIM_SPEED_KMH = 120
const PING_HISTORY_SIZE = 5

const MAP_THEME_COLORS = {
  background: '#F3ECE7',
  landuse: '#EFE6DF',
  building: '#EAD9CE',
  roadCasing: '#FFFFFF',
  roadLine: '#FFFFFF',
  highway: '#F6C453',
  water: '#CFE0EC',
}

const REACHED_PICKUP_STATUSES = [
  'Trip-Arrived-At-Pickup-Location',
  'NavigatingToDropOff',
  'Trip-Arrived-To-Dropped-Off',
  'Trip-Completed',
  'Trip-Completed-Note',
]
const REACHED_DROPOFF_STATUSES = ['Trip-Arrived-To-Dropped-Off', 'Trip-Completed', 'Trip-Completed-Note']

const IN_PROGRESS_STATUSES = [
  'NavigatingToPickup',
  'NavigatingToDropOff',
  'Trip-Arrived-At-Pickup-Location',
  'Trip-Arrived-To-Dropped-Off',
  'Trip-Completed',
  'Trip-Completed-Note',
  'Trip-Started'
]

const ARRIVED_AT_PICKUP_STATUSES = ['Trip-Arrived-At-Pickup-Location']
const ARRIVED_AT_DROPOFF_STATUSES = ['Trip-Arrived-To-Dropped-Off']
const STATUS_REFRESH_REQUEST_THROTTLE_MS = 3000

const FUTURE_LEG_OVERRIDE_STREAK = 3


const MAP_MATCH_PROFILE = 'driving'
const MAP_MATCH_RADIUS_M = 25 // GPS search radius per point, in meters
const MAP_MATCH_MAX_COORDS = 100 // Mapbox Map Matching API hard limit per request
const MIN_MATCH_POINT_SPACING_KM = 0.0015 // drop near-duplicate consecutive points before matching
const ORIENTATION_MIN_CONFIDENCE_KM = 0.05

const SA_LNG_RANGE = [30, 60]
const SA_LAT_RANGE = [14, 35]
function isInRange(value, [min, max]) {
  return typeof value === 'number' && value >= min && value <= max
}
function normalizeCoordinate(coord) {
  if (!Array.isArray(coord) || coord.length < 2) return coord
  const [a, b] = coord
  if (isInRange(a, SA_LNG_RANGE) && isInRange(b, SA_LAT_RANGE)) return [a, b]
  if (isInRange(a, SA_LAT_RANGE) && isInRange(b, SA_LNG_RANGE)) return [b, a]
  return coord
}

function haversineDistanceKm([lng1, lat1], [lng2, lat2]) {
  const R = 6371
  const dLat = ((lat2 - lat1) * Math.PI) / 180
  const dLng = ((lng2 - lng1) * Math.PI) / 180
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLng / 2) ** 2
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
}

function coordsEqual(a, b) {
  if (!Array.isArray(a) || !Array.isArray(b) || a.length < 2 || b.length < 2) return false
  return a[0] === b[0] && a[1] === b[1]
}

function pathsIdentical(prev, next) {
  if (prev.length !== next.length) return false
  for (let i = 0; i < prev.length; i++) {
    if (!coordsEqual(prev[i], next[i])) return false
  }
  return true
}

function lerpCoord(a, b, t) {
  return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t]
}

export default {
  props: {
    data: { type: Object, default: null },
    standalone: { type: Boolean, default: false },
  },
  emits: ['back', 'toggle-stats', 'request-status-refresh'],
  data() {
    return {
      loading: false,
      error: null,
      routes: [],
      liveLocation: null,
      liveLocationUpdatedAt: null,
      locationStale: false,
      currentStatus: null,
      liveLocationEventName: null,
      garageEnums: [],
      directionsCache: {},
      lastStatusRefreshRequestAt: 0,
      futureLegGrowthStreak: 0,
      legPaths: { NavigatingToPickup: [], NavigatingToDropOff: [] },
      legRawPoints: { NavigatingToPickup: [], NavigatingToDropOff: [] },
      legRevealedRawPoints: { NavigatingToPickup: [], NavigatingToDropOff: [] },
      legQueues: { NavigatingToPickup: [], NavigatingToDropOff: [] }, // points still to walk, for whichever leg is active
      knownPointCount: { NavigatingToPickup: 0, NavigatingToDropOff: 0 }, // how many backend points we've already enqueued, per leg
      activeAnimLeg: null, // which leg the queue/animation currently belongs to
      moveLoopRunning: false,
      lastFrameTime: null,
      pingTimestamps: [],
      currentAnimSpeedKmh: DEFAULT_ANIM_SPEED_KMH,

      truckAnimationFrame: null,
      pathAnimationFrame: null,

      isFetchingRoute: false,

      simulationInterval: null,
      simulationRunning: false,

      debugInfo: { hasTruckMarker: false, pickupPoints: 0, dropoffPoints: 0 },

      manualLocations: { garage: null, pickup: null, dropoff: null },

      pickingLocationFor: null,
      route_labels: [
        {
          label: this.$t('navigating-to-pickup'),
          value: 'NavigatingToPickup',
          color: ROUTE_COLORS.NavigatingToPickup,
        },
        {
          label: this.$t('navigating-to-dropoff'),
          value: 'NavigatingToDropOff',
          color: ROUTE_COLORS.NavigatingToDropOff,
        },
      ],
    }
  },
  computed: {
    modalProps() {
      return {
        size: 'xl',
        id: 'mobile-route-map-modal',
        hideFooter: true,
        hideHeader: true,
        dir: 'ltr',
        modalClass: 'mobile-route-map-modal',
      }
    },
    modalEvents() {
      return {
        show: this.onShow,
        hidden: this.onHide,
      }
    },
    isTripCompleted() {
      return this.currentStatus === 'Trip-Completed' || this.currentStatus === 'Trip-Completed-Note'
    },

    debugOverlayEnabled() {
      return DEBUG_OVERLAY
    },
    hasTripStarted() {
      return IN_PROGRESS_STATUSES.includes(this.currentStatus)
    },

    resolvedGarageLocation() {
      if (this.manualLocations.garage) return this.manualLocations.garage
      const direct = this.data?.driver?.garage?.garageLocation?.location
      if (direct) return normalizeCoordinate(direct)

      const garageId = this.data?.garage?._id
      if (!garageId || !this.garageEnums.length) return null
      const match = this.garageEnums.find((g) => String(g.value) === String(garageId))
      return match?.garageLocation?.location ? normalizeCoordinate(match.garageLocation.location) : null
    },
    normalizedStartLocation() {
      if (this.manualLocations.pickup) return this.manualLocations.pickup
      return this.data?.startLocation?.location
        ? normalizeCoordinate(this.data.startLocation.location)
        : null
    },
    normalizedEndLocation() {
      if (this.manualLocations.dropoff) return this.manualLocations.dropoff
      return this.data?.endLocation?.location
        ? normalizeCoordinate(this.data.endLocation.location)
        : null
    },
    reachedPickup() {
      return REACHED_PICKUP_STATUSES.includes(this.currentStatus)
    },
    reachedDropoff() {
      return REACHED_DROPOFF_STATUSES.includes(this.currentStatus)
    },
   
    activeLegAnchors() {
      const reachedOrPastPickup =
        this.currentStatus === 'NavigatingToDropOff' ||
        ARRIVED_AT_DROPOFF_STATUSES.includes(this.currentStatus) ||
        this.isTripCompleted

      if (LEG_DEBUG) {
        console.log('[LEG-DEBUG][activeLegAnchors] evaluating', {
          currentStatus: this.currentStatus,
          reachedOrPastPickup,
          decision: reachedOrPastPickup ? 'NavigatingToDropOff' : 'NavigatingToPickup',
        })
      }

      if (reachedOrPastPickup) {
        return { end: this.normalizedEndLocation, key: 'NavigatingToDropOff' }
      }
      return { end: this.normalizedStartLocation, key: 'NavigatingToPickup' }
    },
    isOnTime() {
      if (typeof this.data?.isDelayed === 'boolean') return !this.data.isDelayed
      return true
    },
    statusMeta() {
      let mainText
      let subText
      let secondaryText

      if (this.isTripCompleted) {
        mainText = this.$t('trip-delivered-success') || 'تم التوصيل بنجاح'
        subText = ''
        secondaryText =
          this.$t('water-delivered-to-beneficiary') || 'تم توصيل المياه الى المستفيد بنجاح'
      } else if (ARRIVED_AT_DROPOFF_STATUSES.includes(this.currentStatus)) {
        mainText = this.$t('arrived-at-beneficiary') || 'وصل السائق الى المستفيد'
        subText = this.$t('delivering-water') || 'جاري تسليم المياه'
        secondaryText = this.$t('driver-at-beneficiary') || 'السائق عند المستفيد'
      } else if (this.currentStatus === 'NavigatingToDropOff') {
        mainText = this.$t('on-the-way') || 'في الطريق'
        subText = this.$t('en-route-to-beneficiary') || 'في الطريق الى المستفيد'
        secondaryText = this.$t('driver-en-route-beneficiary') || 'السائق في طريقه الى المستفيد'
      } else if (ARRIVED_AT_PICKUP_STATUSES.includes(this.currentStatus)) {
        mainText = this.$t('arrived-at-water-source') || 'وصل السائق الى مصدر المياه'
        subText = this.$t('loading-water') || 'جاري تعبئة المياه'
        secondaryText = this.$t('driver-at-water-source') || 'السائق عند مصدر المياه'
      } else if (this.hasTripStarted) {
        mainText = this.$t('on-the-way') || 'في الطريق'
        subText = this.$t('en-route-to-water-source') || 'في الطريق الى مصدر المياه'
        secondaryText = this.$t('driver-en-route-water-source') || 'السائق في طريقه الى مصدر المياه المعتمد'
      } else {
        mainText = this.$t('trip-not-started') || 'لم تبدأ الرحلة بعد'
        subText = this.$t('waiting-for-driver') || 'في انتظار انطلاق السائق'
        secondaryText = this.$t('driver-at-garage') || 'السائق في الكراج'
      }

      return {
        mainText,
        subText,
        secondaryText,
        isOnTime: this.isOnTime,
        badgeVariant: this.isTripCompleted ? 'completed' : this.isOnTime ? 'ontime' : 'delayed',
        badgeText: this.isTripCompleted
          ? this.$t('completed') || 'مكتملة'
          : this.isOnTime
          ? this.$t('on-time') || 'في الوقت المحدد'
          : this.$t('delayed') || 'متأخر',
      }
    },
  },
  watch: {
    'data.status': {
      handler(newStatus, oldStatus) {
        if (!newStatus || newStatus === this.currentStatus) return

        if (LEG_DEBUG) {
          console.log('[LEG-DEBUG][status] data.status changed — updating currentStatus and re-syncing the active leg', {
            from: this.currentStatus,
            to: newStatus,
          })
        }

        this.currentStatus = newStatus
        this.futureLegGrowthStreak = 0
        this.updateCheckmarks()

        if (this.isTripCompleted) {
          this.unsubscribeFromLiveLocation()
        }

        this.refreshTraveledPathFromApi()
      },
    },
    isTripCompleted(val) {
      if (val) {
        this.unsubscribeFromLiveLocation()

        if (this.pathAnimationFrame) {
          cancelAnimationFrame(this.pathAnimationFrame)
          this.pathAnimationFrame = null
        }
        this.moveLoopRunning = false
        this.legQueues = { NavigatingToPickup: [], NavigatingToDropOff: [] }
        this.activeAnimLeg = null

        this.drawFinalRealRoute()
      }
    },
  },
  created() {
    this.staleness_interval = setInterval(this.checkStaleness, 60000)
  },
  mounted() {
    window.simulateLiveTrip = this.simulateLiveTrip.bind(this)
    window.stopSimulation = this.stopSimulation.bind(this)

    window.debugMapState = () => ({
      hasMapInstance: !!map,
      currentStatus: this.currentStatus,
      hasTripStarted: this.hasTripStarted,
      activeLegKey: this.activeLegAnchors?.key,
      routesCount: this.routes.length,
      routesSummary: this.routes.map((r) => ({ leg: r.currentNavigation, points: r.route?.length || 0 })),
      legPathCounts: {
        NavigatingToPickup: this.legPaths.NavigatingToPickup.length,
        NavigatingToDropOff: this.legPaths.NavigatingToDropOff.length,
      },
      legQueueCounts: {
        NavigatingToPickup: this.legQueues.NavigatingToPickup.length,
        NavigatingToDropOff: this.legQueues.NavigatingToDropOff.length,
      },
      activeAnimLeg: this.activeAnimLeg,
      currentAnimSpeedKmh: this.currentAnimSpeedKmh,
      pingTimestamps: this.pingTimestamps,
      hasTruckMarker: !!truck_marker,
      isFetchingRoute: this.isFetchingRoute,
      tripStartDebug: this.describeTripStartPoint(),
    })
    console.log('[MobileRouteMap] Test helpers ready — run simulateLiveTrip() in the console to test.')
    console.log('[MobileRouteMap] Debug helper ready — run debugMapState() in the console to inspect state.')

    if (DEBUG_OVERLAY) {
      this.debugPollInterval = setInterval(() => {
        this.debugInfo = {
          hasTruckMarker: !!truck_marker,
          pickupPoints: this.legPaths.NavigatingToPickup.length,
          dropoffPoints: this.legPaths.NavigatingToDropOff.length,
        }
      }, 500)
    }

    if (this.standalone) {
      this.onShow()
    }
  },
  beforeDestroy() {
    clearInterval(this.staleness_interval)
    clearInterval(this.debugPollInterval)
    this.unsubscribeFromLiveLocation()
    if (this.truckAnimationFrame) cancelAnimationFrame(this.truckAnimationFrame)
    if (this.pathAnimationFrame) cancelAnimationFrame(this.pathAnimationFrame)
    this.stopSimulation()
    delete window.simulateLiveTrip
    delete window.stopSimulation
    delete window.debugMapState
    if (this.standalone) {
      this.onHide()
    }
  },
  methods: {
    closeModal() {
      if (this.standalone) {
        this.$emit('back')
        return
      }
      this.$bvModal.hide('mobile-route-map-modal')
    },
    resolveActiveLegKey() {
      const officialKey = this.activeLegAnchors.key

      if (officialKey === 'NavigatingToPickup' && this.futureLegGrowthStreak >= FUTURE_LEG_OVERRIDE_STREAK) {
        if (LEG_DEBUG) {
          console.log(
            `[LEG-DEBUG][status-override] currentStatus says "${officialKey}" but DropOff has grown ${this.futureLegGrowthStreak} times in a row while Pickup stayed silent — trusting the data over the stale status`
          )
        }
        return 'NavigatingToDropOff'
      }

      return officialKey
    },
    requestStatusRefresh() {
      const now = Date.now()
      if (now - this.lastStatusRefreshRequestAt < STATUS_REFRESH_REQUEST_THROTTLE_MS) return
      this.lastStatusRefreshRequestAt = now

      if (LEG_DEBUG) {
        console.log('[LEG-DEBUG][status-check] currentStatus looks stale — requesting an immediate refresh from the parent')
      }
      this.$emit('request-status-refresh')
    },
    applyFigmaMapTheme() {
      if (!map) return

      const trySetPaint = (layerId, property, value) => {
        try {
          if (map.getLayer(layerId)) map.setPaintProperty(layerId, property, value)
        } catch (e) {}
      }

      trySetPaint('background', 'background-color', MAP_THEME_COLORS.background)

      const layers = map.getStyle()?.layers || []
      layers.forEach((layer) => {
        const id = layer.id.toLowerCase()
        const sourceLayer = (layer['source-layer'] || '').toLowerCase()

        if (layer.type === 'fill' && (id.includes('building') || sourceLayer.includes('building'))) {
          trySetPaint(layer.id, 'fill-color', MAP_THEME_COLORS.building)
        } else if (
          layer.type === 'fill' &&
          (id.includes('landuse') || id.includes('land-use') || sourceLayer.includes('landuse'))
        ) {
          trySetPaint(layer.id, 'fill-color', MAP_THEME_COLORS.landuse)
        } else if (layer.type === 'fill' && (id.includes('water') || sourceLayer.includes('water'))) {
          trySetPaint(layer.id, 'fill-color', MAP_THEME_COLORS.water)
        } else if (layer.type === 'line' && (id.includes('motorway') || id.includes('highway') || id.includes('trunk'))) {
          trySetPaint(layer.id, 'line-color', MAP_THEME_COLORS.highway)
        } else if (layer.type === 'line' && id.includes('road') && id.includes('case')) {
          trySetPaint(layer.id, 'line-color', MAP_THEME_COLORS.roadCasing)
        } else if (layer.type === 'line' && (id.includes('road') || id.includes('street'))) {
          trySetPaint(layer.id, 'line-color', MAP_THEME_COLORS.roadLine)
        }
      })
    },
    zoomIn() {
      if (map) map.zoomIn()
    },
    zoomOut() {
      if (map) map.zoomOut()
    },
    toggleStats() {
      this.$emit('toggle-stats')
    },
    onShow() {
      console.log('[MobileRouteMap][lifecycle] onShow — modal opened for trip:', this.data?._id, {
        status: this.data?.status,
        garageId: this.data?.garage?._id,
      })
      this.currentStatus = this.data?.status || null
      this.liveLocation = this.data?.driver?.mobileDevice?.lastKnownLocation || null
      this.liveLocationUpdatedAt = this.data?.driver?.mobileDevice?.lastTimeLocationUpdated || null
      this.locationStale = this.connectionLost(this.liveLocationUpdatedAt)

      this.legPaths = { NavigatingToPickup: [], NavigatingToDropOff: [] }
      this.legRawPoints = { NavigatingToPickup: [], NavigatingToDropOff: [] }
      this.legRevealedRawPoints = { NavigatingToPickup: [], NavigatingToDropOff: [] }
      this.legQueues = { NavigatingToPickup: [], NavigatingToDropOff: [] }
      this.knownPointCount = { NavigatingToPickup: 0, NavigatingToDropOff: 0 }
      this.activeAnimLeg = null
      this.moveLoopRunning = false
      this.lastFrameTime = null
      this.pingTimestamps = []
      this.currentAnimSpeedKmh = DEFAULT_ANIM_SPEED_KMH
      this.futureLegGrowthStreak = 0

      this.manualLocations = { garage: null, pickup: null, dropoff: null }
      this.pickingLocationFor = null
      console.log('[MobileRouteMap][lifecycle] Initial state:', {
        currentStatus: this.currentStatus,
        hasTripStarted: this.hasTripStarted,
        liveLocation: this.liveLocation,
        liveLocationUpdatedAt: this.liveLocationUpdatedAt,
        locationStale: this.locationStale,
      })
      this.subscribeToLiveLocation()
      this.loadTripData()
    },
    onHide() {
      console.log('[MobileRouteMap][lifecycle] onHide — modal closed, tearing down socket + animations')
      this.unsubscribeFromLiveLocation()
      if (this.truckAnimationFrame) cancelAnimationFrame(this.truckAnimationFrame)
      if (this.pathAnimationFrame) cancelAnimationFrame(this.pathAnimationFrame)
      this.moveLoopRunning = false
      this.stopSimulation()
      if (truck_marker) {
        truck_marker.remove()
        truck_marker = null
        truck_el = null
      }
      if (trip_start_marker) {
        trip_start_marker.remove()
        trip_start_marker = null
      }
    },
    subscribeToLiveLocation() {
      this.unsubscribeFromLiveLocation()
      if (!this.data?._id || this.isTripCompleted) {
        console.log('[MobileRouteMap][socket] Not subscribing — missing trip id or trip already completed', {
          tripId: this.data?._id,
          isTripCompleted: this.isTripCompleted,
        })
        return
      }
      console.log('[MobileRouteMap][socket] Subscribing to event:', `trip-${this.data._id}-live-location`)
      this.liveLocationEventName = subscribeToTripLiveLocation(this.data._id, this.handleLiveLocationUpdate)
    },
    unsubscribeFromLiveLocation() {
      if (this.liveLocationEventName) {
        console.log('[MobileRouteMap][socket] Unsubscribing from event:', this.liveLocationEventName)
        unsubscribeFromTripLiveLocation(this.liveLocationEventName, this.handleLiveLocationUpdate)
        this.liveLocationEventName = null
      }
    },
    recordPingTimestamp() {
      this.pingTimestamps.push(Date.now())
      if (this.pingTimestamps.length > PING_HISTORY_SIZE) {
        this.pingTimestamps.shift()
      }
    },
    averagePingIntervalMs() {
      const times = this.pingTimestamps
      if (times.length < 2) return null
      let total = 0
      for (let i = 1; i < times.length; i++) {
        total += times[i] - times[i - 1]
      }
      return total / (times.length - 1)
    },
    computeAnimSpeedForQueue(legKey) {
      const queue = this.legQueues[legKey]
      if (!queue || !queue.length) return

      let prev = truck_marker
        ? [truck_marker.getLngLat().lng, truck_marker.getLngLat().lat]
        : queue[0].coord

      let distanceKm = 0
      for (const item of queue) {
        distanceKm += haversineDistanceKm(prev, item.coord)
        prev = item.coord
      }

      const avgIntervalMs = this.averagePingIntervalMs()
      if (!avgIntervalMs || avgIntervalMs <= 0) {
        this.currentAnimSpeedKmh = DEFAULT_ANIM_SPEED_KMH
        if (LEG_DEBUG) {
          console.log(`[LEG-DEBUG][speed] leg "${legKey}" — not enough ping history yet, using default speed`, {
            distanceKm: distanceKm.toFixed(3),
            speedKmh: this.currentAnimSpeedKmh,
          })
        }
        return
      }

      const targetHours = avgIntervalMs / 1000 / 3600
      let speed = distanceKm / targetHours
      speed = Math.min(MAX_ANIM_SPEED_KMH, Math.max(MIN_ANIM_SPEED_KMH, speed))

      this.currentAnimSpeedKmh = speed

      if (LEG_DEBUG) {
        console.log(`[LEG-DEBUG][speed] leg "${legKey}" — recalculated adaptive speed`, {
          distanceKm: distanceKm.toFixed(3),
          avgPingIntervalMs: Math.round(avgIntervalMs),
          speedKmh: speed.toFixed(1),
        })
      }
    },
    handleLiveLocationUpdate(payload) {
      console.log('[MobileRouteMap][socket] Event received, raw payload:', payload)

      if (this.isTripCompleted) {
        console.log('[MobileRouteMap][socket] Ignored — trip already completed')
        return
      }
      if (!this.data?._id) {
        console.log('[MobileRouteMap][socket] Ignored — no current trip id on this component')
        return
      }
      if (String(payload?.tripId) !== String(this.data._id)) {
        console.log('[MobileRouteMap][socket] Ignored — tripId mismatch', {
          payloadTripId: payload?.tripId,
          currentTripId: this.data._id,
        })
        return
      }

      const rawLat = Number(payload.latitude)
      const rawLng = Number(payload.longitude)

      if (Number.isNaN(rawLat) || Number.isNaN(rawLng)) {
        console.log('[MobileRouteMap][socket] Ignored — lat/lng missing or not numeric', {
          latitude: payload?.latitude,
          longitude: payload?.longitude,
        })
        return
      }

      const newLocation = normalizeCoordinate([rawLng, rawLat])

      if (LEG_DEBUG) {
        console.log('[LEG-DEBUG][handleLiveLocationUpdate] new ping received', {
          currentStatus: this.currentStatus,
          hasTripStarted: this.hasTripStarted,
          activeLegKey: this.activeLegAnchors.key,
          newLocation,
        })
      }

      this.recordPingTimestamp()

      this.refreshTraveledPathFromApi(newLocation)

      this.liveLocation = newLocation
      this.liveLocationUpdatedAt = payload.timestamp || new Date().toISOString()
      this.locationStale = false
      if (truck_el) truck_el.querySelector('.truck-marker')?.classList.remove('truck-marker--stale')

      console.log('[MobileRouteMap][socket] Applied live location update:', {
        liveLocation: this.liveLocation,
        updatedAt: this.liveLocationUpdatedAt,
      })
    },
    fetchGarageEnums() {
      if (this.garageEnums.length) return Promise.resolve()

      return this.$store
        .dispatch('enums/GET_GARAGE_ENUMS', {})
        .then((list) => {
          this.garageEnums = (list || []).filter((item) => item.value !== null)
        })
        .catch((error) => {
          console.warn('[MobileRouteMap] Failed to fetch garage enums:', error)
        })
    },
    loadTripData() {
      console.log('[MobileRouteMap][data] loadTripData — fetching garage enums + route in parallel')
      this.loading = true
      this.error = null

      Promise.all([this.fetchGarageEnums(), this.getRoute()]).finally(() => {
        console.log('[MobileRouteMap][data] loadTripData complete', {
          garageEnumsCount: this.garageEnums.length,
          routesCount: this.routes.length,
          error: this.error,
        })
        this.loading = false
        this.loadMap()
      })
    },
    getRoute() {
      this.routes = []

      let params = new URLSearchParams()
      params.append('type', 'mobile')

      console.log('[MobileRouteMap][data] getRoute — calling TripRouteService.get', this.data._id)
      return TripRouteService.get(this.data._id, params)
        .then((res) => {
          this.routes = res || []
          console.log(
            '[MobileRouteMap][data] getRoute succeeded, legs returned:',
            this.routes.map((r) => ({ leg: r.currentNavigation, points: r.route?.length || 0 }))
          )
          if (LEG_DEBUG) {
            const garage = this.resolvedGarageLocation
            const pickup = this.normalizedStartLocation
            const dropoff = this.normalizedEndLocation
            const distTo = (pt, anchor) => (pt && anchor ? haversineDistanceKm(pt, anchor).toFixed(3) + 'km' : 'n/a')

            console.log('[LEG-DEBUG][getRoute][orientation-check] raw legs vs currentStatus', {
              currentStatus: this.currentStatus,
              anchors: { garage, pickup, dropoff },
              legs: this.routes.map((r) => {
                const pts = Array.isArray(r.route) ? r.route.map(normalizeCoordinate) : []
                const first = pts[0]
                const last = pts[pts.length - 1]
                return {
                  leg: r.currentNavigation,
                  pointCount: pts.length,
                  firstPoint: first,
                  lastPoint: last,
                  'firstPoint→garage': distTo(first, garage),
                  'firstPoint→pickup': distTo(first, pickup),
                  'firstPoint→dropoff': distTo(first, dropoff),
                  'lastPoint→garage': distTo(last, garage),
                  'lastPoint→pickup': distTo(last, pickup),
                  'lastPoint→dropoff': distTo(last, dropoff),
                }
              }),
            })
          }
        })
        .catch(({ message }) => {
          console.warn('[MobileRouteMap][data] getRoute failed:', message)
          this.error = message
        })
    },
      orientCoordsToAnchors(coords, legKey) {
      const { start, end } = this.getLegAnchors(legKey)
      if (!Array.isArray(coords) || coords.length < 2 || !start || !end) return coords

      const first = coords[0]
      const last = coords[coords.length - 1]

      const firstToStart = haversineDistanceKm(first, start)
      const firstToEnd = haversineDistanceKm(first, end)
      const lastToStart = haversineDistanceKm(last, start)
      const lastToEnd = haversineDistanceKm(last, end)
      const looksReversed =
        firstToEnd + ORIENTATION_MIN_CONFIDENCE_KM < firstToStart &&
        lastToStart + ORIENTATION_MIN_CONFIDENCE_KM < lastToEnd

      if (LEG_DEBUG) {
        console.log(`[LEG-DEBUG][orient] leg "${legKey}" — orientation check`, {
          start,
          end,
          first,
          last,
          firstToStart: firstToStart.toFixed(3),
          firstToEnd: firstToEnd.toFixed(3),
          lastToStart: lastToStart.toFixed(3),
          lastToEnd: lastToEnd.toFixed(3),
          decision: looksReversed ? 'REVERSED — flipping array' : 'looks correctly ordered — leaving as-is',
        })
      }

      if (looksReversed) {
        return coords.slice().reverse()
      }

      return coords
    },
    
    describeTripStartPoint() {
      const pts = this.legRawPoints.NavigatingToPickup
      if (!pts || !pts.length) return { hasPoint: false }
      const start = pts[0]
      const garage = this.resolvedGarageLocation
      const pickup = this.normalizedStartLocation
      const dropoff = this.normalizedEndLocation
      return {
        hasPoint: true,
        coord: start,
        'distanceToGarage(km)': garage ? haversineDistanceKm(start, garage).toFixed(3) : 'n/a',
        'distanceToPickup(km)': pickup ? haversineDistanceKm(start, pickup).toFixed(3) : 'n/a',
        'distanceToDropoff(km)': dropoff ? haversineDistanceKm(start, dropoff).toFixed(3) : 'n/a',
      }
    },
    async drawFinalRealRoute() {
      if (LEG_DEBUG) {
        console.log('[LEG-DEBUG][final] trip completed — drawing the real, final traveled route now')
      }

      for (const legKey of ['NavigatingToPickup', 'NavigatingToDropOff']) {
        const raw = this.legRawPoints[legKey]
        if (!raw || !raw.length) continue

        const matched =
          raw.length > 1
            ? [raw[0], ...(await this.matchSequentialPoints(raw[0], raw.slice(1))).map((p) => p.coord)]
            : raw.slice()

        this.legPaths[legKey] = matched
        this.legRevealedRawPoints[legKey] = raw
        this.updateLegPointsLayer(legKey, raw)
        this.updateLegPathLayer(legKey)
      }

      if (LEG_DEBUG) {
        console.log('[LEG-DEBUG][final][trip-start-marker]', this.describeTripStartPoint())
      }

      const dropoffRaw = this.legRawPoints.NavigatingToDropOff
      const pickupRaw = this.legRawPoints.NavigatingToPickup
      const finalPoint = dropoffRaw?.length
        ? dropoffRaw[dropoffRaw.length - 1]
        : pickupRaw?.length
        ? pickupRaw[pickupRaw.length - 1]
        : this.normalizedEndLocation

      if (finalPoint) this.setTruckMarker(finalPoint)
    },
    async refreshTraveledPathFromApi(fallbackLocation) {
      if (this.isFetchingRoute) {
        console.log('[MobileRouteMap][route] Skipping refetch — one is already in flight')
        return
      }
      this.isFetchingRoute = true
      try {
        await this.getRoute()

        const officialActiveLegKey = this.activeLegAnchors.key
        const activeLegKey = this.resolveActiveLegKey()
        let activeLegHandled = false
        let officialActiveLegGotNewData = false
        let otherLegGotNewData = false

        for (const legKey of ['NavigatingToPickup', 'NavigatingToDropOff']) {
          const element = this.routes.find((r) => r.currentNavigation === legKey)
          let newCoords =
            element && Array.isArray(element.route) && element.route.length
              ? element.route.map(normalizeCoordinate)
              : null

          const legAnchors = this.getLegAnchors(legKey)
          if (newCoords && newCoords.length > 1 && legAnchors.end) {
            const clipped = this.clipCoordsToAnchor(newCoords, legAnchors.end)
            if (clipped.length !== newCoords.length) {
              console.log(
                `[MobileRouteMap][route] Leg "${legKey}" clipped ${newCoords.length - clipped.length} point(s) past its destination anchor`
              )
            }
            newCoords = clipped
          }

        
          if (newCoords && newCoords.length > 1) {
            newCoords = this.orientCoordsToAnchors(newCoords, legKey)
          }

          if (newCoords && newCoords.length > 2 && legAnchors.start) {
            const leadTrimmed = this.clipLeadingPointsToClosestAnchor(newCoords, legAnchors.start)
            if (leadTrimmed.length !== newCoords.length && LEG_DEBUG) {
              console.log(
                `[LEG-DEBUG][lead-clip] leg "${legKey}" — dropped ${newCoords.length - leadTrimmed.length} leading point(s) that didn't belong to this leg (closest-to-start point promoted to index 0)`
              )
            }
            newCoords = leadTrimmed
          }

          if (newCoords && newCoords.length) {
            newCoords = this.snapRouteToAnchors(newCoords, legKey)
          }

          if (!newCoords || !newCoords.length) continue

          const isActive = !this.isTripCompleted && legKey === activeLegKey

          const unchanged = pathsIdentical(this.legRawPoints[legKey], newCoords)
          if (LEG_DEBUG) {
            console.log(`[LEG-DEBUG][refreshTraveledPathFromApi] leg "${legKey}"`, {
              isActive,
              unchanged,
              prevPointCount: this.legRawPoints[legKey].length,
              newPointCount: newCoords.length,
            })
          }
          if (unchanged) {
            if (isActive) activeLegHandled = true
            continue
          }

          if (legKey === officialActiveLegKey) {
            officialActiveLegGotNewData = true
          } else {
            otherLegGotNewData = true
          }

          if (!isActive && !this.isTripCompleted) {
            this.requestStatusRefresh()
          }

          await this.updateLegPath(legKey, newCoords, isActive)
          if (isActive) activeLegHandled = true
        }

        if (!officialActiveLegGotNewData && otherLegGotNewData) {
          this.futureLegGrowthStreak += 1
          if (LEG_DEBUG) {
            console.log(
              `[LEG-DEBUG][status-override] official active leg "${officialActiveLegKey}" silent while the other leg grew — streak now ${this.futureLegGrowthStreak}/${FUTURE_LEG_OVERRIDE_STREAK}`
            )
          }
        } else if (officialActiveLegGotNewData) {
          this.futureLegGrowthStreak = 0
        }

        this.updateCheckmarks()

        for (const legKey of ['NavigatingToPickup', 'NavigatingToDropOff']) {
          if (this.hasArrivedAtLegDestination(legKey) && this.legPaths[legKey].length >= 2) {
            this.updateLegPathLayer(legKey)
          }
        }

        if (!activeLegHandled) {
          if (fallbackLocation && !this.isTripCompleted) {
            console.log('[MobileRouteMap][route] No accumulated points yet for active leg — moving truck directly to raw ping')
            this.animateTruckMarker(fallbackLocation)
          } else if (this.isTripCompleted) {
            this.setTruckMarker(null)
          }
        }

        if (truck_marker && ARRIVED_AT_PICKUP_STATUSES.includes(this.currentStatus)) {
          const cur = truck_marker.getLngLat()
          const target =
            this.legPaths.NavigatingToDropOff[0] || this.normalizedEndLocation
          if (target) {
            const bearing = this.computeBearing([cur.lng, cur.lat], target)
            const icon = truck_el?.querySelector('.marker-symbol')
            if (icon) icon.style.transform = `rotate(${bearing}deg)`
          }
        }

        if (this.isTripCompleted) {
          if (this.pathAnimationFrame) {
            cancelAnimationFrame(this.pathAnimationFrame)
            this.pathAnimationFrame = null
          }
          this.moveLoopRunning = false
          this.legQueues = { NavigatingToPickup: [], NavigatingToDropOff: [] }
          this.activeAnimLeg = null
          await this.drawFinalRealRoute()
        }
      } finally {
        this.isFetchingRoute = false
      }
    },

    dedupeConsecutiveCoords(coords) {
      const result = []
      for (const c of coords) {
        const last = result[result.length - 1]
        if (!last || haversineDistanceKm(last, c) > MIN_MATCH_POINT_SPACING_KM) {
          result.push(c)
        }
      }
      return result
    },

    async matchCoordinatesToRoads(coords) {
      const cleaned = this.dedupeConsecutiveCoords(coords)
      if (cleaned.length < 2) return cleaned

      const trimmed =
        cleaned.length > MAP_MATCH_MAX_COORDS ? cleaned.slice(cleaned.length - MAP_MATCH_MAX_COORDS) : cleaned

      const coordString = trimmed.map((c) => `${c[0]},${c[1]}`).join(';')
      const radiuses = trimmed.map(() => MAP_MATCH_RADIUS_M).join(';')
      const url =
        `https://api.mapbox.com/matching/v5/mapbox/${MAP_MATCH_PROFILE}/${coordString}` +
        `?geometries=geojson&overview=full&radiuses=${radiuses}&tidy=true&access_token=${mapboxgl.accessToken}`

      try {
        const res = await fetch(url)
        const json = await res.json()
        const matchedCoords = json?.matchings?.[0]?.geometry?.coordinates

        if (Array.isArray(matchedCoords) && matchedCoords.length >= 2) {
          if (LEG_DEBUG) {
            console.log('[LEG-DEBUG][mapmatch] snapped to roads', {
              inputPoints: trimmed.length,
              outputPoints: matchedCoords.length,
            })
          }
          return matchedCoords
        }

        if (LEG_DEBUG) {
          console.log('[LEG-DEBUG][mapmatch] no usable match, falling back to raw points', {
            code: json?.code,
            message: json?.message,
          })
        }
        return trimmed
      } catch (err) {
        console.warn('[MobileRouteMap][mapmatch] Map Matching request failed, falling back to raw points:', err)
        return trimmed
      }
    },

    async matchSequentialPoints(contextPoint, points) {
      let ctx = contextPoint
      let combined = []

      for (const pt of points) {
        const segment = await this.matchHopToRoad(ctx, pt)
        const toAdd = segment.length > 1 ? segment.slice(1) : segment
        toAdd.forEach((coord, idx) => {
          combined.push({ coord, isRaw: idx === toAdd.length - 1 })
        })
        ctx = pt
      }

      return combined
    },

    async matchHopToRoad(ctx, pt) {
      const matched = await this.matchCoordinatesToRoads([ctx, pt])
      if (Array.isArray(matched) && matched.length > 2) return matched

      const directions = await this.fetchDirectionsRoute(ctx, pt)
      return Array.isArray(directions) && directions.length > 2 ? directions : matched
    },

    async streamPointsIntoQueue(legKey, contextPoint, points) {
      let ctx = contextPoint

      for (const pt of points) {
        const segment = await this.matchHopToRoad(ctx, pt)
        const toAdd = segment.length > 1 ? segment.slice(1) : segment
        const tagged = toAdd.map((coord, idx) => ({ coord, isRaw: idx === toAdd.length - 1 }))

        this.legQueues[legKey] = this.legQueues[legKey].concat(tagged)
        this.computeAnimSpeedForQueue(legKey)
        this.ensureMoveLoop()

        ctx = pt
      }
    },

    async ensureLegHasDrawablePath(legKey, points, rawPointCount = 2) {
      const looksLikeRealMatch = Array.isArray(points) && points.length > Math.max(rawPointCount, 2)
      if (looksLikeRealMatch) return points

      const { start, end } = this.getLegAnchors(legKey)
      if (!start || !end) return points || []

      if (LEG_DEBUG) {
        console.log(
          `[LEG-DEBUG][fallback] leg "${legKey}" only had ${points ? points.length : 0} point(s) after matching (from ${rawPointCount} raw) — fetching a full anchor-to-anchor route so a line always renders`
        )
      }

      const cacheKey = `${legKey}:${start.join(',')}:${end.join(',')}`
      if (this.directionsCache[cacheKey]) return this.directionsCache[cacheKey]

      const directions = await this.fetchDirectionsRoute(start, end)

      if (Array.isArray(directions) && directions.length > 2) {
        this.directionsCache[cacheKey] = directions
      } else if (LEG_DEBUG) {
        console.log(
          `[LEG-DEBUG][fallback] leg "${legKey}" got only a straight-line fallback (Directions API unavailable) — NOT caching, will retry next time`
        )
      }

      return directions
    },

    async updateLegPath(legKey, newCoords, isActive) {
      this.legRawPoints[legKey] = newCoords

      if (legKey === 'NavigatingToPickup' && LEG_DEBUG) {
        console.log('[LEG-DEBUG][updateLegPath][trip-start-marker] Pickup leg stored — this is what the red marker will use', {
          firstPoint: newCoords[0],
          pointCount: newCoords.length,
        })
      }

      if (this.isTripCompleted) isActive = false

      if (!isActive) {
        const legOrder = ['NavigatingToPickup', 'NavigatingToDropOff']
        const isFutureLeg =
          !this.isTripCompleted && legOrder.indexOf(legKey) > legOrder.indexOf(this.activeLegAnchors.key)

        if (isFutureLeg) {
          if (LEG_DEBUG) {
            console.log(
              `[LEG-DEBUG][future-leg] leg "${legKey}" hasn't started yet (still on "${this.activeLegAnchors.key}") — drawing nothing for it, not even a preview`
            )
          }
          return
        }

        let matched =
          newCoords.length > 1
            ? [newCoords[0], ...(await this.matchSequentialPoints(newCoords[0], newCoords.slice(1))).map((p) => p.coord)]
            : newCoords
        matched = await this.ensureLegHasDrawablePath(legKey, matched, newCoords.length)
        this.legPaths[legKey] = matched
        this.legRevealedRawPoints[legKey] = newCoords
        this.updateLegPointsLayer(legKey, this.legRevealedRawPoints[legKey])
        this.updateLegPathLayer(legKey)
        return
      }

      const already = this.knownPointCount[legKey]
      if (newCoords.length <= already) {
        if (LEG_DEBUG) {
          console.log(`[LEG-DEBUG][queue] leg "${legKey}" — no new points beyond index ${already}, skipping`)
        }
        return
      }

      const newPts = newCoords.slice(already)
      this.knownPointCount[legKey] = newCoords.length

      if (this.activeAnimLeg !== legKey) {
        this.activeAnimLeg = legKey

        if (!truck_marker) {
          if (LEG_DEBUG) {
            console.log(
              `[LEG-DEBUG][queue] first-ever truck placement for "${legKey}" — starting at the real first known point and animating through all ${newPts.length} raw pt(s) since, instead of jumping straight to the latest one`
            )
          }
          this.setTruckMarker(newPts[0])
          this.legPaths[legKey] = [newPts[0]]
          this.legRevealedRawPoints[legKey] = [newPts[0]]
          this.legQueues[legKey] = []
          await this.streamPointsIntoQueue(legKey, newPts[0], newPts.slice(1))
        } else {
          if (LEG_DEBUG) {
            console.log(`[LEG-DEBUG][queue] switching active leg to "${legKey}" — queue reset, truck continues animating from its current (road-matched) position`)
          }
          const cur = truck_marker.getLngLat()
          const contextPoint = [cur.lng, cur.lat]

          this.legQueues[legKey] = []
          this.legPaths[legKey] = [contextPoint]
          await this.streamPointsIntoQueue(legKey, contextPoint, newPts)
        }
      } else {
        const queueTail = this.legQueues[legKey].length
          ? this.legQueues[legKey][this.legQueues[legKey].length - 1].coord
          : truck_marker
          ? [truck_marker.getLngLat().lng, truck_marker.getLngLat().lat]
          : newPts[0]

        await this.streamPointsIntoQueue(legKey, queueTail, newPts)
      }

      if (LEG_DEBUG) {
        console.log(`[LEG-DEBUG][queue] leg "${legKey}" — enqueued ${newPts.length} new raw point(s), queue length now ${this.legQueues[legKey].length}`)
      }

      this.computeAnimSpeedForQueue(legKey)
      this.ensureMoveLoop()
    },

    hasArrivedAtLegDestination(legKey) {
      if (legKey === 'NavigatingToPickup') {
        return (
          ARRIVED_AT_PICKUP_STATUSES.includes(this.currentStatus) ||
          REACHED_PICKUP_STATUSES.includes(this.currentStatus)
        )
      }
      if (legKey === 'NavigatingToDropOff') {
        return (
          ARRIVED_AT_DROPOFF_STATUSES.includes(this.currentStatus) ||
          REACHED_DROPOFF_STATUSES.includes(this.currentStatus)
        )
      }
      return false
    },
    ensureMoveLoop() {
      if (this.isTripCompleted) return
      if (this.moveLoopRunning) return
      this.moveLoopRunning = true
      this.lastFrameTime = null
      this.pathAnimationFrame = requestAnimationFrame((now) => this.moveLoop(now))
    },

    moveLoop(now) {
      if (this.isTripCompleted) {
        this.moveLoopRunning = false
        this.legQueues = { NavigatingToPickup: [], NavigatingToDropOff: [] }
        this.activeAnimLeg = null
        return
      }

      if (this.lastFrameTime == null) this.lastFrameTime = now
      const dt = (now - this.lastFrameTime) / 1000
      this.lastFrameTime = now

      const legKey = this.activeAnimLeg
      const queue = legKey ? this.legQueues[legKey] : null

      if (legKey && queue && queue.length && truck_marker) {
        const speedKmh = this.currentAnimSpeedKmh || DEFAULT_ANIM_SPEED_KMH
        const kmPerSec = speedKmh / 3600
        let remainingKm = kmPerSec * dt
        let moved = false
        let revealedNewGreenPoint = false
        const cur = truck_marker.getLngLat()
        let curArr = [cur.lng, cur.lat]

        while (remainingKm > 0 && queue.length) {
          const nextItem = queue[0]
          const nextPt = nextItem.coord
          const d = haversineDistanceKm(curArr, nextPt)
          if (d <= remainingKm || d < 0.001) {
            curArr = nextPt
            this.legPaths[legKey] = this.legPaths[legKey].concat([nextPt])
            queue.shift()
            remainingKm -= d
            moved = true

            if (nextItem.isRaw) {
              this.legRevealedRawPoints[legKey] = this.legRevealedRawPoints[legKey].concat([nextPt])
              revealedNewGreenPoint = true
            }
          } else {
            curArr = lerpCoord(curArr, nextPt, remainingKm / d)
            const base = this.legPaths[legKey]
            const lastIsInterpolated =
              base.length && !queue.some((p) => coordsEqual(p.coord, base[base.length - 1]))
            this.legPaths[legKey] = (lastIsInterpolated ? base.slice(0, -1) : base).concat([curArr])
            remainingKm = 0
            moved = true
          }
        }

        if (moved) {
          const bearing = this.computeBearing([cur.lng, cur.lat], curArr)
          truck_marker.setLngLat(curArr)
          const icon = truck_el?.querySelector('.marker-symbol')
          if (icon) icon.style.transform = `rotate(${bearing}deg)`
          // Green waypoint dots have been removed; we still track which
          // raw points were "revealed" (legRevealedRawPoints) since that
          // bookkeeping is used elsewhere (drawFinalRealRoute), but we no
          // longer call updateLegPointsLayer on every reveal here — it now
          // only ever draws the single red trip-start marker, which
          // doesn't change as the truck moves.
        }
      }

      const stillHasWork = legKey && this.legQueues[legKey] && this.legQueues[legKey].length
      if (stillHasWork) {
        this.pathAnimationFrame = requestAnimationFrame((n) => this.moveLoop(n))
      } else {
        this.moveLoopRunning = false
        if (legKey && this.hasArrivedAtLegDestination(legKey)) {
          if (LEG_DEBUG) {
            console.log(`[LEG-DEBUG][arrival] leg "${legKey}" — truck has arrived, drawing the full walked line now`)
          }
          this.updateLegPathLayer(legKey)
        }
      }
    },

    clipCoordsToAnchor(coords, anchor) {
      const CLOSE_ENOUGH_KM = 0.05
      for (let i = coords.length - 1; i >= 0; i--) {
        if (haversineDistanceKm(coords[i], anchor) <= CLOSE_ENOUGH_KM) {
          return coords.slice(0, i + 1)
        }
      }
      return coords
    },
    // THE FIX (route/truck appears to double back to the departure point
    // before heading to the beneficiary): mirrors clipCoordsToAnchor, but
    // trims the FRONT of the array instead of the back.
    //
    // A leg's raw points should get monotonically farther from its own
    // start anchor as the truck actually departs. If instead the array
    // opens with a few points that are close-ish to the start anchor,
    // then wander further away, then come back close again before really
    // leaving — that "closer, farther, closer" wobble at the front means
    // those earliest points are leftovers that don't belong to this leg
    // (e.g. lingering pings from while the truck was still finishing the
    // previous leg / parked at the pickup point). Rendered as-is, they
    // draw a visible loop back toward the earlier departure point before
    // the line/animation continues on to the real destination.
    //
    // We scan a small leading window, find the point that sits CLOSEST to
    // this leg's start anchor, and drop everything before it — so the
    // kept array always begins at (or right next to) the true start of
    // this leg's actual travel.
    clipLeadingPointsToClosestAnchor(coords, anchor) {
      if (!Array.isArray(coords) || coords.length < 3 || !anchor) return coords

      const LEADING_SEARCH_WINDOW = Math.min(coords.length - 1, 30)
      let closestIdx = 0
      let closestDist = haversineDistanceKm(coords[0], anchor)

      for (let i = 1; i <= LEADING_SEARCH_WINDOW; i++) {
        const d = haversineDistanceKm(coords[i], anchor)
        if (d < closestDist) {
          closestDist = d
          closestIdx = i
        }
      }

      if (closestIdx === 0) return coords
      return coords.slice(closestIdx)
    },
    loadMap() {
      setTimeout(() => {
        if (document.getElementById('mobile-route-map')) {
          console.log('[MobileRouteMap][map] loadMap — container found, initializing mapboxgl.Map')
          mapboxgl.accessToken = this.$config.mapBoxPK

          if (mapboxgl.getRTLTextPluginStatus() === 'unavailable') {
            mapboxgl.setRTLTextPlugin(
              'https://api.mapbox.com/mapbox-gl-js/plugins/mapbox-gl-rtl-text/v0.2.3/mapbox-gl-rtl-text.js',
              null,
              true
            )
          }

          map = new mapboxgl.Map({
            container: 'mobile-route-map',
            style: 'mapbox://styles/mapbox/light-v11',
            center: [46.715022884817245, 24.642516582053403],
            zoom: 8,
            language: this.isLangRtl ? 'ar' : 'en',
          })

          if (truck_marker) truck_marker.remove()
          truck_marker = null
          truck_el = null

          map.on('click', (e) => {
            if (!this.pickingLocationFor) return
            const clicked = [e.lngLat.lng, e.lngLat.lat]
            this.manualLocations = { ...this.manualLocations, [this.pickingLocationFor]: clicked }
            console.log(
              `[MobileRouteMap][debug] Manually set "${this.pickingLocationFor}" location to:`,
              clicked
            )
            this.pickingLocationFor = null
            this.addLocationMarkers()
          })

          map.on('style.load', async () => {
            console.log('[MobileRouteMap][map] style.load fired — running theme + markers pipeline')
            this.applyFigmaMapTheme()
            console.log('[MobileRouteMap][map] theme applied')
            this.addLocationMarkers()
            console.log('[MobileRouteMap][map] location markers added')

            await this.refreshTraveledPathFromApi()
            if (this.isTripCompleted) {
              await this.drawFinalRealRoute()
            }
            await this.fitMapToTrip()
            console.log('[MobileRouteMap][map] map pipeline complete, fitted to trip bounds')
          })
        }
      })
    },
    getLegAnchors(navigationKey) {
      if (navigationKey === 'NavigatingToPickup') {
        return { start: this.resolvedGarageLocation, end: this.normalizedStartLocation }
      }
      if (navigationKey === 'NavigatingToDropOff') {
        return { start: this.normalizedStartLocation, end: this.normalizedEndLocation }
      }
      return { start: null, end: null }
    },
    snapRouteToAnchors(coordinates, navigationKey) {
      const { start, end } = this.getLegAnchors(navigationKey)
      const SNAP_THRESHOLD_KM = 0.03
      const MAX_ANCHOR_SNAP_KM = 3
      let result = coordinates.slice()

      if (start && result.length) {
        const distanceToStart = haversineDistanceKm(start, result[0])
        if (distanceToStart > SNAP_THRESHOLD_KM && distanceToStart <= MAX_ANCHOR_SNAP_KM) {
          result.unshift(start)
        } else if (distanceToStart > MAX_ANCHOR_SNAP_KM && LEG_DEBUG) {
          console.log(
            `[LEG-DEBUG][snap] leg "${navigationKey}" — start anchor is ${distanceToStart.toFixed(1)}km from the nearest real point, NOT force-snapping (too far to be real)`
          )
        }
      } else if (start && !result.length) {
        result = [start]
      }

      if (end && result.length) {
        const distanceToEnd = haversineDistanceKm(end, result[result.length - 1])
        if (distanceToEnd > SNAP_THRESHOLD_KM && distanceToEnd <= MAX_ANCHOR_SNAP_KM) {
          result.push(end)
        } else if (distanceToEnd > MAX_ANCHOR_SNAP_KM && LEG_DEBUG) {
          console.log(
            `[LEG-DEBUG][snap] leg "${navigationKey}" — end anchor is ${distanceToEnd.toFixed(1)}km from the nearest real point, NOT force-snapping (too far to be real)`
          )
        }
      } else if (end && !result.length) {
        result = [end]
      }

      return result
    },
    fetchDirectionsRoute(start, end) {
      const coordString = `${start[0]},${start[1]};${end[0]},${end[1]}`
      const url = `https://api.mapbox.com/directions/v5/mapbox/driving/${coordString}?geometries=geojson&overview=full&access_token=${mapboxgl.accessToken}`

      return fetch(url)
        .then((res) => res.json())
        .then((responseData) => {
          const coords = responseData?.routes?.[0]?.geometry?.coordinates
          if (Array.isArray(coords) && coords.length >= 2) return coords
          return [start, end]
        })
        .catch((error) => {
          console.warn(
            '[MobileRouteMap] Directions API request failed, falling back to a straight line:',
            error
          )
          return [start, end]
        })
    },
    isRouteTooSparse(coordinates) {
      if (coordinates.length < 2) return true
      const totalDistanceKm = haversineDistanceKm(coordinates[0], coordinates[coordinates.length - 1])
      const MIN_POINTS = 10
      const expectedMinPoints = Math.max(MIN_POINTS, Math.ceil(totalDistanceKm))
      return coordinates.length < expectedMinPoints
    },
    async getLegCoordinatesAsync(sourceId) {
      if (!this.routes.length) return null

      const element = this.routes.find((r) => r.currentNavigation === sourceId)
      const { start, end } = this.getLegAnchors(sourceId)

      if (element && Array.isArray(element.route) && element.route.length) {
        const raw = element.route.map(normalizeCoordinate)
        if (!this.isRouteTooSparse(raw)) {
          return this.snapRouteToAnchors(raw, sourceId)
        }
      }

      if (!start || !end) return null

      const cacheKey = `${sourceId}:${start.join(',')}:${end.join(',')}`
      if (this.directionsCache[cacheKey]) return this.directionsCache[cacheKey]

      const coords = await this.fetchDirectionsRoute(start, end)
      this.directionsCache[cacheKey] = coords
      return coords
    },
    simulateLiveTrip(from, to, { steps = 25, intervalMs = 1200, curve } = {}) {
      this.stopSimulation()

      const start = from || this.liveLocation || this.normalizedStartLocation
      const end = to || this.normalizedStartLocation || this.normalizedEndLocation

      if (!start || !end) {
        console.warn(
          '[simulateLiveTrip] No usable start/end coordinates found — pass them explicitly, e.g. simulateLiveTrip([46.71,24.64],[46.72,24.65])'
        )
        return
      }

      if (!this.data?._id) {
        console.warn('[simulateLiveTrip] No active trip (this.data._id) — open a trip first.')
        return
      }

      const dx = end[0] - start[0]
      const dy = end[1] - start[1]
      const dist = Math.hypot(dx, dy) || 1
      const perp = [-dy / dist, dx / dist]
      const curveAmount = curve != null ? curve : dist * 0.15

      let step = 0
      this.simulationRunning = true
      console.log(`[simulateLiveTrip] starting: ${steps} pings every ${intervalMs}ms`)

      this.simulationInterval = setInterval(() => {
        step++
        const t = step / steps
        const wobble = Math.sin(t * Math.PI) * curveAmount
        const lng = start[0] + dx * t + perp[0] * wobble
        const lat = start[1] + dy * t + perp[1] * wobble

        this.handleLiveLocationUpdate({
          tripId: this.data._id,
          latitude: lat,
          longitude: lng,
          timestamp: new Date().toISOString(),
        })

        console.log(`[simulateLiveTrip] ping ${step}/${steps} ->`, [lng, lat])

        if (step >= steps) this.stopSimulation()
      }, intervalMs)
    },
    stopSimulation() {
      if (this.simulationInterval) {
        clearInterval(this.simulationInterval)
        this.simulationInterval = null
      }
      this.simulationRunning = false
    },
    computeBearing([lng1, lat1], [lng2, lat2]) {
      const toRad = (d) => (d * Math.PI) / 180
      const toDeg = (r) => (r * 180) / Math.PI
      const dLng = toRad(lng2 - lng1)
      const y = Math.sin(dLng) * Math.cos(toRad(lat2))
      const x =
        Math.cos(toRad(lat1)) * Math.sin(toRad(lat2)) -
        Math.sin(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.cos(dLng)
      return (toDeg(Math.atan2(y, x)) + 360) % 360
    },
    animateTruckMarker(newLocation) {
      if (this.isTripCompleted) return

      if (!truck_marker || !map) {
        this.setTruckMarker(newLocation)
        return
      }

      const start = truck_marker.getLngLat()
      const startPos = [start.lng, start.lat]
      const endPos = newLocation
      const DURATION_MS = 900

      if (this.truckAnimationFrame) cancelAnimationFrame(this.truckAnimationFrame)
      const startTime = performance.now()
      const bearing = this.computeBearing(startPos, endPos)
      const icon = truck_el?.querySelector('.marker-symbol')

      const step = (now) => {
        const t = Math.min((now - startTime) / DURATION_MS, 1)
        const lng = startPos[0] + (endPos[0] - startPos[0]) * t
        const lat = startPos[1] + (endPos[1] - startPos[1]) * t
        truck_marker.setLngLat([lng, lat])

        if (icon) icon.style.transform = `rotate(${bearing}deg)`

        if (t < 1) {
          this.truckAnimationFrame = requestAnimationFrame(step)
        } else {
          this.truckAnimationFrame = null
        }
      }
      this.truckAnimationFrame = requestAnimationFrame(step)
    },
    updateLegPathLayer(legKey) {
      if (!this.isTripCompleted) return
      const coords = this.legPaths[legKey]
      if (!map || !coords || coords.length < 2) return

      const sourceId = `path-${legKey}`
      const lineColor = ROUTE_COLORS[legKey] || TRAVELED_PATH_COLOR

      const geojson = {
        type: 'Feature',
        properties: {},
        geometry: {
          type: 'LineString',
          coordinates: coords,
        },
      }

      if (map.getSource(sourceId)) {
        map.getSource(sourceId).setData(geojson)
      } else {
        map.addSource(sourceId, {
          type: 'geojson',
          data: geojson,
        })

        map.addLayer({
          id: `${sourceId}-casing`,
          type: 'line',
          source: sourceId,
          layout: { 'line-join': 'round', 'line-cap': 'round' },
          paint: { 'line-color': '#ffffff', 'line-width': 8, 'line-opacity': 0.95 },
        })

        map.addLayer({
          id: `${sourceId}-line`,
          type: 'line',
          source: sourceId,
          layout: { 'line-join': 'round', 'line-cap': 'round' },
          paint: { 'line-color': lineColor, 'line-width': 4.5, 'line-opacity': 1 },
        })
      }

      this.bringLegPointsToFront(legKey)
    },
    // THE FIX (green waypoint dots removed): this used to draw a green
    // circle for EVERY point in `coords`, plus one larger red circle for
    // index 0 of the Pickup leg. Per request, only that single red
    // "trip start" marker survives — everything else (the green dots and
    // their optional text labels) has been removed entirely. `coords` is
    // still accepted for API-compatibility with existing call sites, but
    // only `coords[0]` is ever used, and only for the Pickup leg.
    updateLegPointsLayer(legKey, coords) {
      if (!this.isTripCompleted) return
      if (!SHOW_TRIP_START_MARKER || !map) return

      const sourceId = `points-${legKey}`
      const startPoint = legKey === 'NavigatingToPickup' && coords && coords.length ? coords[0] : null

      if (LEG_DEBUG && legKey === 'NavigatingToPickup') {
        console.log('[LEG-DEBUG][trip-start-marker] rendering red marker at', {
          startPoint,
          distanceToGarage: this.resolvedGarageLocation
            ? haversineDistanceKm(startPoint, this.resolvedGarageLocation).toFixed(3) + 'km'
            : 'n/a',
        })
      }

      const geojson = {
        type: 'FeatureCollection',
        features: startPoint
          ? [
              {
                type: 'Feature',
                properties: { isStart: 1 },
                geometry: { type: 'Point', coordinates: startPoint },
              },
            ]
          : [],
      }

      if (map.getSource(sourceId)) {
        map.getSource(sourceId).setData(geojson)
        this.bringLegPointsToFront(legKey)
        return
      }

      map.addSource(sourceId, { type: 'geojson', data: geojson })

      map.addLayer({
        id: `${sourceId}-start-circle`,
        type: 'circle',
        source: sourceId,
        filter: ['==', ['get', 'isStart'], 1],
        paint: {
          'circle-radius': 11,
          'circle-color': TRIP_START_POINT_COLOR,
          'circle-stroke-width': 3,
          'circle-stroke-color': '#ffffff',
        },
      })

      this.bringLegPointsToFront(legKey)
    },
    // Only the single red start-circle layer remains now that the green
    // waypoint dots (and their labels) have been removed.
    bringLegPointsToFront(legKey) {
      if (!map) return
      const sourceId = `points-${legKey}`
      const id = `${sourceId}-start-circle`
      if (map.getLayer(id)) {
        try {
          map.moveLayer(id)
        } catch (e) {
          // Layer might not be fully registered yet on a very fast
          // successive call — harmless, the next update re-tries.
        }
      }
    },
    async fitMapToTrip() {
      const hasTraveledData =
        this.legPaths.NavigatingToPickup.length || this.legPaths.NavigatingToDropOff.length
      if (hasTraveledData) {
        return this.fitMapToTraveledPath()
      }
      return this.fitMapToTripLegs()
    },
    fitMapToTraveledPath() {
      const primaryPoints = [
        this.normalizedStartLocation,
        this.normalizedEndLocation,
        ...this.legPaths.NavigatingToPickup,
        ...this.legPaths.NavigatingToDropOff,
      ].filter(Boolean)

      if (!primaryPoints.length) return

      const primaryBounds = primaryPoints.reduce(
        (b, coord) => b.extend(coord),
        new mapboxgl.LngLatBounds(primaryPoints[0], primaryPoints[0])
      )

      const finalBounds = this.maybeIncludeGarage(primaryBounds)
      console.log('[MobileRouteMap][map] fitMapToTraveledPath — calling fitBounds')
      map.fitBounds(finalBounds, { padding: 60, maxZoom: 16 })
    },
    async fitMapToTripLegs() {
      const legKeys = ['NavigatingToPickup', 'NavigatingToDropOff']
      const legPoints = []
      for (const sourceId of legKeys) {
        const coords = await this.getLegCoordinatesAsync(sourceId)
        if (coords) legPoints.push(...coords)
      }

      const primaryPoints = [this.normalizedStartLocation, this.normalizedEndLocation, ...legPoints].filter(
        Boolean
      )

      if (!primaryPoints.length) return

      const primaryBounds = primaryPoints.reduce(
        (b, coord) => b.extend(coord),
        new mapboxgl.LngLatBounds(primaryPoints[0], primaryPoints[0])
      )

      const finalBounds = this.maybeIncludeGarage(primaryBounds)
      console.log('[MobileRouteMap][map] fitMapToTripLegs — calling fitBounds')
      map.fitBounds(finalBounds, { padding: 60, maxZoom: 16 })
    },
    maybeIncludeGarage(primaryBounds) {
      const sw = primaryBounds.getSouthWest()
      const ne = primaryBounds.getNorthEast()
      const primaryDiagonalKm = haversineDistanceKm([sw.lng, sw.lat], [ne.lng, ne.lat])
      const center = primaryBounds.getCenter()

      const garageLocation = this.resolvedGarageLocation
      if (!garageLocation) return primaryBounds

      const distanceToGarageKm = haversineDistanceKm([center.lng, center.lat], garageLocation)
      const threshold = Math.max(primaryDiagonalKm * 5, 5)
      if (distanceToGarageKm <= threshold) {
        console.log('[MobileRouteMap][map] Garage included in auto-zoom bounds', { distanceToGarageKm, threshold })
        return primaryBounds.extend(garageLocation)
      }
      console.log('[MobileRouteMap][map] Garage excluded from auto-zoom (too far)', { distanceToGarageKm, threshold })
      return primaryBounds
    },
    buildMarkerElement(iconClass, iconSrc, labelText, coord) {
      const wrapper = document.createElement('div')
      wrapper.className = 'map-marker-wrapper-outer'

      const pill = document.createElement('div')
      pill.className = 'marker-pill'
      pill.innerHTML = `<span class="marker-pill-check">&#10003;</span><span>${labelText}</span>`

      const circle = document.createElement('div')
      circle.className = `map-marker-wrapper ${iconClass}`
      circle.innerHTML = `<img src="${iconSrc}" class="marker-symbol" alt="${labelText}">`

      wrapper.appendChild(pill)
      wrapper.appendChild(circle)

      return { wrapper, pill }
    },
    addLocationMarkers() {
      const garageLocation = this.resolvedGarageLocation
      console.log('[MobileRouteMap][markers] Resolved anchor locations:', {
        garage: garageLocation,
        pickup: this.normalizedStartLocation,
        dropoff: this.normalizedEndLocation,
      })
      if (!garageLocation) {
        console.warn(
          '[MobileRouteMap] Could not resolve a garage location — either garage._id is missing on trip data, or no matching entry was found in garageEnums.'
        )
      }
      if (garageLocation) {
        const { wrapper: yard_el, pill: yard_pill } = this.buildMarkerElement(
          'yard-marker',
          require('../../assets/images/icons/grageicon.png'),
          this.$t('garage-location') || 'الكراج',
          garageLocation
        )
        this.yard_pill = yard_pill
        if (yard_marker) yard_marker.remove()
        yard_marker = new mapboxgl.Marker(yard_el).setLngLat(garageLocation).addTo(map)
      }

      if (this.normalizedStartLocation) {
        const { wrapper: pickup_el, pill: pickup_pill } = this.buildMarkerElement(
          'pickup-marker',
          require('../../assets/images/icons/watersourceicon.png'),
          this.$t('pick-up-location') || 'مصدر المياه',
          this.normalizedStartLocation
        )
        this.pickup_pill = pickup_pill
        if (pickup_marker) pickup_marker.remove()
        pickup_marker = new mapboxgl.Marker(pickup_el).setLngLat(this.normalizedStartLocation).addTo(map)
      }

      if (this.normalizedEndLocation) {
        const { wrapper: dropoff_el, pill: dropoff_pill } = this.buildMarkerElement(
          'dropoff-marker',
          require('../../assets/images/icons/homeicon.png'),
          this.$t('drop-off-location') || 'المستفيد',
          this.normalizedEndLocation
        )
        this.dropoff_pill = dropoff_pill
        if (dropoff_marker) dropoff_marker.remove()
        dropoff_marker = new mapboxgl.Marker(dropoff_el).setLngLat(this.normalizedEndLocation).addTo(map)
      }

      this.updateCheckmarks()
    },
    updateCheckmarks() {
      if (this.yard_pill) {
        this.yard_pill.classList.toggle('marker-pill--reached', this.hasTripStarted)
      }
      if (this.pickup_pill) {
        this.pickup_pill.classList.toggle('marker-pill--reached', this.reachedPickup)
      }
      if (this.dropoff_pill) {
        this.dropoff_pill.classList.toggle('marker-pill--reached', this.reachedDropoff)
      }
    },
    setTruckMarker(location) {
      if (!map) return

      const completedFallback = this.normalizedEndLocation
      const fallback = this.isTripCompleted ? completedFallback : null

      const resolvedLocation = location || fallback
      if (!resolvedLocation) {
        console.log('[MobileRouteMap][truck] setTruckMarker — no location or fallback available, skipping')
        return
      }

      if (truck_marker) {
        console.log('[MobileRouteMap][truck] setTruckMarker — direct set (existing marker):', resolvedLocation)
        truck_marker.setLngLat(resolvedLocation)
        return
      }

      console.log('[MobileRouteMap][truck] setTruckMarker — creating marker at:', resolvedLocation, {
        usedFallback: !location,
        isTripCompleted: this.isTripCompleted,
      })

      truck_el = document.createElement('div')
      truck_el.className = 'truck-marker-wrapper-outer'
      truck_el.innerHTML = `
        <div class="truck-marker">
          <img src="${require('../../assets/images/icons/tanker.png')}" class="marker-symbol">
        </div>
      `

      truck_marker = new mapboxgl.Marker(truck_el).setLngLat(resolvedLocation).addTo(map)

      if (this.locationStale) {
        truck_el.querySelector('.truck-marker')?.classList.add('truck-marker--stale')
      }
    },
    connectionLost(date) {
      if (!date) return true
      const now = moment()
      const last = moment(date)
      return now.diff(last, 'minutes') > 5
    },
    checkStaleness() {
      if (this.isTripCompleted) return
      const wasStale = this.locationStale
      this.locationStale = this.connectionLost(this.liveLocationUpdatedAt)
      if (this.locationStale !== wasStale) {
        console.log('[MobileRouteMap][staleness] Staleness changed:', {
          stale: this.locationStale,
          lastUpdate: this.liveLocationUpdatedAt,
        })
      }
      if (truck_el) {
        truck_el.querySelector('.truck-marker')?.classList.toggle('truck-marker--stale', this.locationStale)
      }
    },
  },
  i18n: {
    messages: {
      en: require('@/services/i18n/locales/en/manageTrips.json'),
      ar: require('@/services/i18n/locales/ar/manageTrips.json'),
    },
  },
}
</script>

<style lang="scss" scoped>
.mobile-route-map-page {
  width: 100%;
  min-height: 100vh;
  background: #ffffff;
}

.map-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid #eef0f2;

  .modal-title {
    margin: 0;
    font-weight: 700;
    font-size: 1.05rem;
  }

  .close-btn {
    background: none;
    border: none;
    font-size: 1.4rem;
    line-height: 1;
    color: #6b7280;
    cursor: pointer;
    padding: 0.25rem;
  }
}

.map-canvas-wrap {
  position: relative;
}

.route-legend {
  border-top: 1px solid #eef0f2;
}

.route-name {
  font-size: 1.1rem;
  font-weight: 700;
  margin: 0;
}

.legend-item {
  margin: 0 1.25rem;
  gap: 15px;
}

.legend-swatch {
  display: inline-block;
  width: 18px;
  height: 18px;
  border-radius: 4px;
  margin-right: 0.5rem;
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.08);
}

.legend-label {
  font-size: 0.95rem;
  font-weight: 500;
}

.stale-banner {
  position: absolute;
  top: 12px;
  left: 50%;
  transform: translateX(-50%);
  background-color: #fff4e5;
  color: #7a4a00;
  font-size: 0.85rem;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  text-align: center;
  z-index: 5;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.12);
}

.zoom-controls {
  position: absolute;
  left: 16px;
  bottom: 130px;
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border-radius: 6px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  z-index: 5;
}

.zoom-btn {
  width: 30px;
  height: 30px;
  border: none;
  background: #ffffff;
  font-size: 1.1rem;
  line-height: 1;
  cursor: pointer;

  &:hover {
    background: #f3f4f6;
  }
}

.zoom-divider {
  height: 1px;
  background: #e5e7eb;
}

.stats-toggle-btn {
  position: absolute;
  left: 16px;
  bottom: 108px;
  width: 26px;
  height: 22px;
  border: none;
  border-radius: 4px;
  background: #ffffff;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
  cursor: pointer;
  z-index: 5;
  font-size: 0.75rem;
}

.trip-status-card {
    background: #ffffff;
    border-radius: 12px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.18);
    display: flex;
    align-items: flex-end;
    justify-content: flex-start;
    width: 400px;
    margin: 33px;
    position: absolute;
    bottom: 0px;
  .back-arrow-btn {
    border: none;
    background: #f9fafb;
    color: #6b7280;
    font-size: 1.2rem;
    width: 32px;
    flex-shrink: 0;
    cursor: pointer;
  }

  .status-card-body {
    flex: 1;
    padding: 0.75rem 0.9rem;
  }

  .status-card-top {
    display: flex;
    justify-content: flex-end;
    margin-bottom: 0.25rem;
  }

  .status-badge {
    font-size: 0.7rem;
    font-weight: 600;
    padding: 0.15rem 0.5rem;
    border-radius: 999px;

    &--ontime {
      background: #e7f6ec;
      color: #1e7d3c;
    }

    &--delayed {
      background: #fdecec;
      color: #c0392b;
    }

    &--completed {
      background: #e7f6ec;
      color: #1e7d3c;
    }
  }

  .status-card-main {
    display: flex;
    align-items: center;
    gap: 0.6rem;
  }

  .truck-illustration {
    font-size: 1.8rem;
    line-height: 1;
  }

  .completed-icon {
    width: 73px;
  }

  .status-main-text {
    margin: 0;
    font-weight: 700;
    font-size: 0.95rem;
    color: #111827;
  }

  .status-sub-text {
    margin: 0;
    font-size: 0.78rem;
    color: #6b7280;
  }

  .status-card-footer {
    margin-top: 0.5rem;
    padding-top: 0.4rem;
    border-top: 1px solid #f0f1f3;
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-size: 0.75rem;
    color: #6b7280;
  }
}
</style>

<style lang="scss">
.map-marker-wrapper-outer {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.marker-pill {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  background: #ffffff;
  border: 1px solid #d1d5db;
  border-radius: 999px;
  padding: 0.15rem 0.55rem;
  font-size: 0.72rem;
  font-weight: 600;
  color: #374151;
  white-space: nowrap;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);

  .marker-pill-check {
    display: none;
    color: #1e7d3c;
    font-size: 0.7rem;
  }

  &--reached {
    border-color: #2e7d32;
    color: #1e7d3c;

    .marker-pill-check {
      display: inline;
    }
  }
}

.truck-marker-wrapper-outer {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.truck-marker {
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: opacity 0.2s ease;
}

.truck-marker .marker-symbol {
  width: 26px;
  height: 26px;
  transition: transform 0.3s linear;
}

.truck-marker--stale {
  opacity: 0.5;
}
</style>